import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Gift } from 'lucide-react';
import { LOGO_URL, SCHOOL } from '../lib/constants';
import { useLeadModal } from '../lib/leadModalContext';
import { trackEvent } from '../lib/analytics';

const DISMISSED_KEY = 'ukk_exit_intent_dismissed';
const SESSION_STARTED_KEY = 'ukk_exit_intent_session_started';

const ExitIntentModal = () => {
  const { openLeadModal } = useLeadModal();
  const [open, setOpen] = useState(false);

  const alreadyDismissed = useCallback(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (_) {
      return false;
    }
  }, []);

  const markDismissed = useCallback(() => {
    try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch (_) {}
  }, []);

  const trigger = useCallback((source) => {
    if (alreadyDismissed()) return;
    setOpen(true);
    markDismissed();
    trackEvent('exit_intent_shown', { source });
  }, [alreadyDismissed, markDismissed]);

  useEffect(() => {
    if (alreadyDismissed()) return;

    // Record session start
    try {
      if (!sessionStorage.getItem(SESSION_STARTED_KEY)) {
        sessionStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
      }
    } catch (_) {}

    const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 640px)').matches;

    // Desktop: fire on mouseleave from top edge
    const onMouseLeave = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) {
        trigger('mouse-top');
      }
    };

    // Mobile: fire on fast scroll-up near top after 15s on page
    let lastY = window.scrollY;
    let lastT = Date.now();
    const onScroll = () => {
      try {
        const started = Number(sessionStorage.getItem(SESSION_STARTED_KEY) || '0');
        if (!started || Date.now() - started < 15000) {
          lastY = window.scrollY;
          lastT = Date.now();
          return;
        }
        const y = window.scrollY;
        const dy = lastY - y; // positive = scrolling up
        const dt = Date.now() - lastT;
        if (dy > 220 && dt < 300 && y < 400) {
          trigger('mobile-scroll-up');
        }
        lastY = y;
        lastT = Date.now();
      } catch (_) {}
    };

    if (isMobile) {
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [alreadyDismissed, trigger]);

  const handleClaim = () => {
    setOpen(false);
    trackEvent('exit_intent_claim');
    openLeadModal('exit-intent-popup');
  };

  const handleClose = () => {
    setOpen(false);
    trackEvent('exit_intent_dismiss');
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm"
            onClick={handleClose}
            data-testid="exit-intent-backdrop"
          />
          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.24 }}
            className="fixed z-[10000] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-md panel-solid rounded-2xl overflow-hidden border border-[color:var(--neon-red)]/45"
            style={{ boxShadow: '0 0 0 1px rgba(255,42,42,0.35), 0 30px 80px rgba(0,0,0,0.75)' }}
            data-testid="exit-intent-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-intent-title"
          >
            {/* Header */}
            <div className="relative px-6 pt-8 pb-5 text-center">
              <button
                onClick={handleClose}
                aria-label="Close popup"
                data-testid="exit-intent-close"
                className="absolute right-3 top-3 h-8 w-8 grid place-items-center rounded-lg text-white/70 hover:bg-white/5"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="mx-auto h-16 w-16 rounded-full overflow-hidden glow-red-sm mb-4 border border-[color:var(--neon-red)]/40">
                <img src={LOGO_URL} alt="" className="h-full w-full object-cover" />
              </div>

              <div className="inline-flex items-center gap-1.5 border border-[color:var(--neon-red)]/50 text-[color:var(--neon-red)] font-mono-ui uppercase tracking-[0.22em] text-[10px] rounded-full px-2.5 py-1 mb-3">
                <Gift className="h-3 w-3" /> Wait — Free Gift
              </div>

              <h3
                id="exit-intent-title"
                className="font-display text-2xl sm:text-3xl text-white tracking-tight leading-tight"
              >
                Don&#39;t leave empty-handed.
              </h3>
              <p className="mt-2 text-white/75 text-sm leading-relaxed">
                Claim <span className="text-white font-semibold">2 FREE Classes</span>{' '}
                <span className="text-white/60">+</span>{' '}
                a <span className="text-white font-semibold">FREE Uniform</span> when you book
                your trial today. No commitment. Zero pressure.
              </p>
            </div>

            {/* CTA row */}
            <div className="px-6 pb-6 space-y-2">
              <button
                onClick={handleClaim}
                data-testid="exit-intent-claim-button"
                className="w-full h-12 rounded-xl bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] active:scale-[0.99] text-white font-display uppercase tracking-[0.14em] text-sm flex items-center justify-center gap-2 glow-red-md transition-transform"
              >
                <Sparkles className="h-4 w-4" /> Claim My Free Trial
              </button>
              <button
                onClick={handleClose}
                data-testid="exit-intent-dismiss-button"
                className="w-full h-10 rounded-xl border border-white/15 text-white/70 hover:bg-white/5 font-mono-ui uppercase tracking-[0.18em] text-[11px]"
              >
                No thanks
              </button>
              <p className="text-center text-[11px] text-white/45 font-mono-ui pt-1">
                {SCHOOL.address}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentModal;
